import { useState, type ReactNode } from 'react'
import {
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  type OpenChangeReason,
} from '@floating-ui/react'
import type { LevelPopoverMethod } from './levelHelpers'

interface LevelPopoverProps {
  /** Full level title ("Level 2: Fill a classroom with readers"); also the popover heading. */
  title: string
  description?: string
  /** Accessible label for the trigger button. */
  ariaLabel: string
  /** `start` left-aligns the popover with the trigger (badges); `end` right-aligns it (right circle). */
  align: 'start' | 'end'
  /** Classes for the trigger button. */
  triggerClassName: string
  /** Trigger content (the icon). */
  children: ReactNode
  /** Called each time the popover opens, with how it was opened. */
  onOpen: (method: LevelPopoverMethod) => void
}

/**
 * Level popover. Hover or keyboard focus shows it; click pins it open until a
 * second click on the same trigger, an outside click, or Escape.
 *
 * Built on @floating-ui/react rather than Headless UI, whose Popover is
 * click-only (ux-components Rule 8 exception, ARD decision #10).
 */
export default function LevelPopover({
  title,
  description,
  ariaLabel,
  align,
  triggerClassName,
  children,
  onOpen,
}: LevelPopoverProps) {
  const [open, setOpen] = useState(false)
  const [pinned, setPinned] = useState(false)
  // Elements kept in state (not floating-ui's ref setters) so render never reads a ref.
  const [reference, setReference] = useState<HTMLButtonElement | null>(null)
  const [floating, setFloating] = useState<HTMLDivElement | null>(null)
  const [arrowElement, setArrowElement] = useState<SVGSVGElement | null>(null)

  const show = (method: LevelPopoverMethod) => {
    if (!open) onOpen(method)
    setOpen(true)
  }

  const { floatingStyles, context } = useFloating({
    open,
    elements: { reference, floating },
    placement: align === 'start' ? 'top-start' : 'top-end',
    whileElementsMounted: autoUpdate,
    middleware: [offset(12), flip({ padding: 8 }), shift({ padding: 8 }), arrow({ element: arrowElement })],
    onOpenChange: (nextOpen: boolean, _event?: Event, reason?: OpenChangeReason) => {
      if (nextOpen) {
        if (reason === 'hover' || reason === 'focus') show(reason)
        return
      }
      // Leaving or blurring a pinned popover keeps it open; dismissals always close it.
      if (pinned && (reason === 'hover' || reason === 'safe-polygon' || reason === 'focus-out')) return
      setPinned(false)
      setOpen(false)
    },
  })

  const hover = useHover(context, { move: false })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role])

  const togglePin = () => {
    if (pinned) {
      setPinned(false)
      setOpen(false)
      return
    }
    // A click always counts as its own open (pinning), even if hover already showed the popover.
    setPinned(true)
    setOpen(true)
    onOpen('click')
  }

  return (
    <>
      <button
        ref={setReference}
        type='button'
        aria-label={ariaLabel}
        className={triggerClassName}
        {...getReferenceProps({ onClick: togglePin })}
      >
        {children}
      </button>

      {open && (
        <FloatingPortal>
          <div
            ref={setFloating}
            style={floatingStyles}
            className='z-50 flex w-70 flex-col gap-1.5 rounded-lg bg-alt px-4.5 py-4 text-left shadow-xl'
            {...getFloatingProps()}
          >
            <p className='text-cta font-sans text-base font-bold'>{title}</p>
            {description && <p className='text-secondary-txt font-sans text-sm leading-snug'>{description}</p>}
            <FloatingArrow
              ref={setArrowElement}
              context={context}
              width={16}
              height={8}
              staticOffset={align === 'start' ? 16 : 28}
              className='fill-alt'
            />
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
